'use client';
/**
 * NewsletterBand.tsx — Client Component island
 * Multi-step newsletter signup form for the homepage §2 band.
 * Cycle 76-B: extracted from HomepageClient.tsx as a standalone client island.
 *
 * Uses useNewsletterSubmit + useMailcheck hooks (shared with NewsletterForm.tsx).
 * Data: newsletterForm prop from page.tsx server component (content/homepage/index.json).
 */

import { useRef } from 'react';
import { useNewsletterSubmit } from '@/hooks/useNewsletterSubmit';
import { useMailcheck } from '@/hooks/useMailcheck';
import { useTurnstile } from '@/hooks/useTurnstile';
import OrbitSpinner from '@/components/OrbitSpinner';

const CheckIcon = () => (
  <svg width={32} height={32} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ color: 'var(--gold)' }}>
    <polyline points="4 12 10 18 20 6" />
  </svg>
);

interface NewsletterFormData {
  heading?: string | null;
  subheading?: string | null;
  placeholder?: string | null;
  submitButton?: string | null;
  errorState?: string | null;
  doneStateSubscribed?: string | null;
  doneStateActive?: string | null;
  doneStateResubscribed?: string | null;
}

interface Props {
  newsletterForm: NewsletterFormData;
}

export default function NewsletterBand({ newsletterForm }: Props) {
  const formRef = useRef<HTMLFormElement>(null);
  const formRenderTimeRef = useRef<number>(Date.now());

  const { checkOnSubmit: heroMailcheckOnSubmit } = useMailcheck({
    emailInputId: 'hero-email',
    suggestionDivId: 'hero-email-suggestion',
  });

  // Hotfix 2026-07-09: hero form was never wired to Turnstile, so every hero
  // submit POSTed with an empty token and the server 403'd with
  // "Verification failed." (masked by the generic errorState string below).
  // Mirrors the footer form's wiring in components/NewsletterForm.tsx verbatim.
  const { containerRef: turnstileRef, getToken: getTurnstileToken, resetWidget: resetTurnstile } = useTurnstile();

  const { isSubmitting: heroIsSubmitting, submitError: heroSubmitError, doneVariant: heroDoneVariant, submittedEmail: heroSubmittedEmail } = useNewsletterSubmit({
    formRef,
    formRenderTimeRef,
    emailInputId: 'hero-email',
    nameInputId: 'hero-name',
    mailcheckOnSubmit: heroMailcheckOnSubmit,
    getTurnstileToken,
    resetTurnstile,
    // 2026-07-24: this is the homepage hero form — the exact form that went
    // silently down 2026-05-27→07-09. See lib/form-event-log.ts.
    formSource: 'hero',
  });

  return (
    <section className="hp-newsletter-band" id="newsletter" aria-labelledby="nl-band-heading">
      <div className="hp-newsletter-band-inner">
        <h2 id="nl-band-heading" className="hp-nl-heading">
          {(() => {
            const heading = newsletterForm?.heading ?? 'Piano Fantasy Minute — twice a week, in your inbox.';
            const GOLD_PHRASE = 'Piano Fantasy Minute';
            const idx = heading.indexOf(GOLD_PHRASE);
            if (idx === -1) return heading;
            const before = heading.slice(0, idx);
            const after = heading.slice(idx + GOLD_PHRASE.length);
            return (
              <>
                {before && <span style={{ color: 'var(--text)' }}>{before}</span>}
                <span style={{ color: 'var(--gold)' }}>{GOLD_PHRASE}</span>
                {after && <span style={{ color: 'var(--text)' }}>{after}</span>}
              </>
            );
          })()}
        </h2>
        {newsletterForm?.subheading && (
          <p className="nl-subheading">{newsletterForm.subheading}</p>
        )}
        <form
          ref={formRef}
          className="email-card js-multistep"
          data-state="email"
          noValidate
          onSubmit={(e) => e.preventDefault()}
        >
          {/* Honeypot — Layer 2 */}
          <div className="hp-field" aria-hidden="true">
            <label htmlFor="hero-website">Website</label>
            <input type="text" id="hero-website" name="website" tabIndex={-1} autoComplete="off" />
          </div>
          <div className="form-state" data-step="email">
            <label className="sr-only" htmlFor="hero-email">Email address</label>
            <div className="email-input-wrap">
              <input id="hero-email" className="email-input" type="email" placeholder={newsletterForm?.placeholder ?? 'Your email'} required autoComplete="email" />
              <div className="mailcheck-suggestion" id="hero-email-suggestion" aria-live="polite" />
            </div>
            <button type="submit" className="btn btn-primary">{newsletterForm?.submitButton ?? 'Send it to me'}</button>
          </div>
          {/* Step 2: first name */}
          <div className="form-state" data-step="name">
            <span className="nl-step-indicator" aria-hidden="true">Step 2 of 2</span>
            <h2>What&apos;s your first name?</h2>
            <p className="nl-step2-subtext">We use it to greet you personally — nothing else.</p>
            <label className="sr-only" htmlFor="hero-name">First name</label>
            <input id="hero-name" className="email-input" type="text" placeholder="Your first name" autoComplete="given-name" required />
            {heroSubmitError && (
              <p className="nl-error" role="alert" aria-live="polite">{heroSubmitError || newsletterForm?.errorState}</p>
            )}
            <button type="submit" className="btn btn-primary" disabled={heroIsSubmitting} aria-busy={heroIsSubmitting}>
              {heroIsSubmitting ? (
                <span className="lv-inline-loading">
                  <OrbitSpinner size={16} aria-label="Signing you up" />
                  Signing you up…
                </span>
              ) : 'Complete sign-up →'}
            </button>
            <button type="button" className="form-back js-back">&#8592; change email</button>
          </div>
          {/* done state */}
          <div className="form-state form-done" data-step="done">
            <CheckIcon />
            {heroDoneVariant === 'active' ? (
              <>
                <h2>{newsletterForm?.doneStateActive ?? "Already on the list. Next one's on its way."}</h2>
                {heroSubmittedEmail && <p className="nl-done-email">Sent to: <strong>{heroSubmittedEmail}</strong></p>}
              </>
            ) : heroDoneVariant === 'resubscribed' ? (
              <>
                <h2>{newsletterForm?.doneStateResubscribed ?? 'Back on the list. Glad to have you.'}</h2>
                {heroSubmittedEmail && <p className="nl-done-email">Sent to: <strong>{heroSubmittedEmail}</strong></p>}
              </>
            ) : (
              <>
                <h2>{newsletterForm?.doneStateSubscribed ?? "You're in. First Minute arrives within the week."}</h2>
                {heroSubmittedEmail && <p className="nl-done-email">Sent to: <strong>{heroSubmittedEmail}</strong></p>}
              </>
            )}
          </div>

          {/* Hotfix 2026-07-09: off-screen Turnstile widget container — copied verbatim
              from components/NewsletterForm.tsx:162-166 (footer form). */}
          <div
            ref={turnstileRef}
            aria-hidden="true"
            style={{ position: 'absolute', left: '-10000px', top: 'auto', width: '1px', height: '1px', overflow: 'hidden' }}
          />
        </form>
      </div>
    </section>
  );
}
